import type { BackPattern } from "@/lib/tick-id";

/**
 * A tick drawn at a real size.
 *
 * `mm` is the published body length and `pxPerMm` comes from the screen calibration, so
 * a nymph really is drawn about a millimetre and a half across. That is the whole point:
 * the reason nymph bites go unnoticed is that people are looking for something the size
 * of the picture they were shown.
 *
 * Stylised on purpose — these are shapes, not photographs, and the flow says so.
 */
export function TickFigure({
  mm,
  pxPerMm,
  pattern = "plain-no-markings",
  legs = 8,
  darkLegs = false,
  engorged = false,
  label,
}: {
  mm: number;
  pxPerMm: number;
  pattern?: BackPattern;
  legs?: 6 | 8;
  darkLegs?: boolean;
  engorged?: boolean;
  label?: string;
}) {
  const size = Math.max(4, mm * pxPerMm);

  return (
    <svg
      viewBox="0 0 120 120"
      style={{ width: size, height: size }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      className="shrink-0 overflow-visible"
    >
      <TickShape pattern={pattern} legs={legs} darkLegs={darkLegs} engorged={engorged} />
    </svg>
  );
}

/**
 * The drawing itself, in a 120 x 120 user space with the head at the top. Rendered bare
 * so it can be placed inside another SVG (a ruler, a magnified inset) as well as inside
 * TickFigure's own box.
 */
export function TickShape({
  pattern = "plain-no-markings",
  legs = 8,
  darkLegs = false,
  engorged = false,
}: {
  pattern?: BackPattern;
  legs?: 6 | 8;
  darkLegs?: boolean;
  engorged?: boolean;
}) {
  const perSide = legs / 2;
  const legYs = perSide === 4 ? [40, 50, 60, 70] : [44, 56, 68];
  // The legs and mouthparts are dark brown on paper, which vanishes on the dark theme's
  // green-black page; there they lift to the body's own rust (or, for a dark-legged
  // species, a grey that still reads darker than the body).
  const legClass = darkLegs
    ? "stroke-[oklch(0.25_0.02_280)] dark:stroke-[oklch(0.42_0.02_280)]"
    : "stroke-[oklch(0.32_0.03_60)] dark:stroke-rust";
  const headClass = darkLegs
    ? "fill-[oklch(0.3_0.03_60)] dark:fill-[oklch(0.42_0.02_280)]"
    : "fill-[oklch(0.3_0.03_60)] dark:fill-rust";
  const body = engorged ? "oklch(0.55 0.04 90)" : "oklch(0.5 0.09 45)";
  const scutum = "oklch(0.42 0.08 40)";
  // A fed tick swells mostly behind the shield, so the body rounds out and the shield
  // stays the same small size.
  const bodyRy = engorged ? 40 : 34;

  return (
    <>
      <g className={legClass} strokeWidth="3.5" strokeLinecap="round" fill="none">
        {legYs.map((y, i) => (
          <g key={i}>
            <path d={`M48 ${y} C 30 ${y - 8}, 22 ${y - 10}, 12 ${y - 4}`} />
            <path d={`M72 ${y} C 90 ${y - 8}, 98 ${y - 10}, 108 ${y - 4}`} />
          </g>
        ))}
      </g>
      <ellipse cx="60" cy="32" rx="7" ry="9" className={headClass} />
      <ellipse cx="60" cy={engorged ? 70 : 66} rx={engorged ? 36 : 30} ry={bodyRy} fill={body} />
      {!engorged && <Markings pattern={pattern} scutum={scutum} />}
      {engorged && <ellipse cx="60" cy="44" rx="16" ry="12" fill={scutum} opacity="0.85" />}
    </>
  );
}

function Markings({ pattern, scutum }: { pattern: BackPattern; scutum: string }) {
  const cream = "oklch(0.95 0.02 90)";
  switch (pattern) {
    case "single-white-dot":
      return (
        <>
          <ellipse cx="60" cy="50" rx="22" ry="18" fill={scutum} opacity="0.9" />
          <circle cx="60" cy="62" r="7" fill={cream} />
        </>
      );
    case "scattered-white-flecks":
      return (
        <>
          <ellipse cx="60" cy="50" rx="22" ry="18" fill={scutum} opacity="0.9" />
          {[
            [44, 84],
            [60, 92],
            [76, 84],
            [38, 72],
            [82, 72],
          ].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r="3.2" fill={cream} />
          ))}
        </>
      );
    case "solid-dark-shield":
      return <ellipse cx="60" cy="48" rx="24" ry="20" fill="oklch(0.28 0.02 280)" />;
    case "marbled-silver":
      return (
        <>
          <ellipse cx="60" cy="48" rx="24" ry="19" fill="oklch(0.8 0.02 95)" />
          <ellipse cx="52" cy="44" rx="7" ry="5" fill={scutum} opacity="0.55" />
          <ellipse cx="68" cy="52" rx="6" ry="4" fill={scutum} opacity="0.55" />
          <ellipse cx="62" cy="40" rx="4" ry="3" fill={scutum} opacity="0.45" />
        </>
      );
    case "silver-stripes":
      return (
        <>
          <ellipse cx="60" cy="48" rx="24" ry="19" fill={scutum} />
          <rect x="57" y="32" width="6" height="32" rx="3" fill={cream} />
          <rect x="42" y="38" width="5" height="22" rx="2.5" fill={cream} opacity="0.9" />
          <rect x="73" y="38" width="5" height="22" rx="2.5" fill={cream} opacity="0.9" />
        </>
      );
    default:
      return <ellipse cx="60" cy="50" rx="22" ry="18" fill={scutum} opacity="0.55" />;
  }
}
